import { useCallback, useState } from 'react'

import { useTranslations } from 'next-intl'

import { Alert } from '@/components/Alert'
import { Button } from '@/components/buttons/Button'
import { LOGIN_STATUS } from '@/features/user/userSlice'

import { PinInput } from './PinInput'

import styles from './LoginSteps.module.scss'

interface LoginStepsProps {
  loginStatus: LOGIN_STATUS
  error: string | null
  loading: boolean
  onEmailSubmit: (email: string) => void
  onPinSubmit: (pin: string) => void
  onPinCancel: () => void
}

const onEnterHandler = (cb: () => void) => (e: React.KeyboardEvent) => {
  if (e.key === 'Enter') {
    e.preventDefault()
    cb()
  }
}

const EMPTY_PIN = Array(6).fill('')

export const LoginSteps: React.FC<LoginStepsProps> = ({
  loginStatus,
  error,
  loading,
  onEmailSubmit,
  onPinSubmit,
  onPinCancel,
}) => {
  const [email, setEmail] = useState('')
  const [pin, setPin] = useState<string[]>(EMPTY_PIN)

  const submitEmail = useCallback(() => {
    setPin(EMPTY_PIN)
    onEmailSubmit(email)
  }, [email, onEmailSubmit])

  const submitPin = useCallback(() => {
    onPinSubmit(pin.join(''))
  }, [pin, onPinSubmit])

  const t = useTranslations()

  return (
    <>
      {error ? (
        <Alert
          title={t('login_error_pin_title')}
          body={t('login_error_pin_body')}
          type={'Error'}
          className={styles.ErrorAlert}
          data-cy="login-error"
        />
      ) : null}
      {loginStatus === LOGIN_STATUS.LOGGED_OUT ? (
        <div className={styles.EmailInput}>
          <label htmlFor="email">{t('login_email')}</label>
          <input
            name="email"
            id="email"
            type="email"
            autoFocus
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={onEnterHandler(submitEmail)}
            value={email}
            placeholder={t('login_enter')}
          />
        </div>
      ) : (
        <PinInput pin={pin} onChange={(pin) => setPin(pin)} onKeyPress={onEnterHandler(submitPin)} />
      )}
      {loginStatus === LOGIN_STATUS.LOGGED_OUT ? (
        <Button
          data-cy="email-login-button"
          disabled={!email}
          loading={loading}
          onClick={submitEmail}
          content={t('login_title')}
        />
      ) : (
        <div className={styles.PinActionButtons}>
          <Button type="OutlineSecondary" onClick={onPinCancel} content={t('pin_cancel')} />
          <Button
            data-cy="pin-login-button"
            loading={loading}
            disabled={pin.join('').length < 6}
            onClick={submitPin}
            content={t('pin_submit')}
          />
        </div>
      )}
    </>
  )
}
